<template>
  <span
    class="editor-button relative inline-flex items-center justify-center overflow-hidden rounded-xl min-h-10 max-w-full min-w-0 px-5 py-2 text-base font-medium transition duration-150 ease-in-out select-none"
    :class="buttonClass"
    :style="useEditorFontFamily(button.textFontFamily)"
  >
    <span
      class="editor-button__text inline-flex min-w-0 max-w-full items-center whitespace-normal break-words text-center"
    >
      {{ button.text }}
    </span>
  </span>
</template>

<script setup lang="ts">
import type { ButtonStyleInterface } from '../common'

type ButtonWidth = 'content' | 'full' | 'pixels'

interface DecorativeButtonInterface
  extends Partial<Omit<ButtonStyleInterface, 'type'>> {
  widthDesktop?: ButtonWidth
  widthDesktopPx?: number
  widthMobile?: ButtonWidth
  widthMobilePx?: number
}

interface PropsInterface {
  button: DecorativeButtonInterface
}

const props = defineProps<PropsInterface>()

const buttonClass = computed(() => {
  let classNames =
    props.button.look === 'border'
      ? 'editor-button_border'
      : 'editor-button_solid'

  classNames += props.button.colorHover
    ? ' editor-button_hover-color'
    : ' editor-button_no-hover-color'

  return classNames
})

const color = computed(() => props.button.color)
const colorHover = computed(() => props.button.colorHover)
const textColor = computed(() => props.button.textColor || '#ffffff')

const getButtonWidth = (width?: ButtonWidth, widthPx?: number) => {
  if (width === 'full') {
    return '100%'
  }

  if (width === 'pixels' && Number.isFinite(widthPx) && Number(widthPx) > 0) {
    return `${widthPx}px`
  }

  return 'auto'
}

const buttonWidthMobile = computed(() =>
  getButtonWidth(props.button.widthMobile, props.button.widthMobilePx)
)
const buttonWidthDesktop = computed(() =>
  getButtonWidth(props.button.widthDesktop, props.button.widthDesktopPx)
)
</script>

<style lang="scss" scoped>
.editor-button {
  width: v-bind(buttonWidthMobile);
  max-width: 100%;

  @media (min-width: 768px) {
    width: v-bind(buttonWidthDesktop);
  }

  &__text {
    overflow-wrap: anywhere;
  }

  &_border {
    color: v-bind(color);
    border: 2px solid v-bind(color);

    &.editor-button_hover-color:hover {
      color: v-bind(colorHover);
      border-color: v-bind(colorHover);
    }
  }

  &_solid {
    background-color: v-bind(color);
    color: v-bind(textColor);

    &.editor-button_hover-color:hover {
      background-color: v-bind(colorHover);
    }
  }

  &_no-hover-color:hover {
    filter: brightness(1.5);
  }
}
</style>
